import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import Tesseract from 'tesseract.js';
import axios from 'axios';
import { useAppId } from '../context/AppIdContext';
import { useNavigate } from 'react-router-dom';
import '../style/scan.css';
import '../style/speakSearch.css';
import Header from '../component/header';
import WhatsappSlipButton from '../component/WhatsappSlipButton';
import PracharButton from '../component/PracharButton';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ScanId = () => {
    const webcamRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [foundId, setFoundId] = useState("");

    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);

    // --- Voter Fetching State ---
    const { appId, language, userId } = useAppId();
    const [voter, setVoter] = useState(null);
    const [fetchingVoter, setFetchingVoter] = useState(false);

    // --- Camera State ---
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState({});

    const navigate = useNavigate();

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Scan And Search",
            subtitle: "Point your camera at a Voter ID card to automatically fetch details.",
            startScanning: "Start Scanning",
            retakePhoto: "Retake Photo",
            scanModalTitle: "Scan ID Card",
            cameraLabel: "Camera",
            autoScanning: "Auto-scanning... hold steady",
            idFound: "ID Found:",
            mobileNotAvailable: "Mobile number not available.",
            voterNotFound: "ID {id} scanned, but voter details not found.",
            fetchError: "Error retrieving voter details.",
            fetchFamilyFailed: "Could not fetch family details.",
            messageError: "Error generating message.",
            fetchingDetails: "Fetching Voter Details...",
            readyToScan: "Ready to scan voter records",
            noActiveScan: "No Active Scan",
            clickToStart: 'Click the "Start Scanning" button to begin.',
            record: "Record",
            n_a: "N/A",
            details: "Details :",
            fatherHusband: "Father/Husband :",
            sexAge: "Sex/Age :",
            mobile: "Mobile :",
            idCard: "ID Card :",
            boothNo: "Booth No. :",
            houseNo: "House No. :",
            pollingLocation: "Polling Location :",
            call: "Call",
            text: "Text",
            slip: "Slip",
            prachar: "Prachar",
            family: "Family",
            addToGroup: "Add to Group",
            address: "Address :",
            serialNo: "Serial No. :",
            addToGroupConfirm: "Are you sure you want to ADD this member to your group?",
            addToGroupSuccess: "Added to group successfully!",
            addToGroupFailed: "Failed to add to group.",
            addToGroupError: "An error occurred while adding to the group."
        },
        hi: {
            title: "स्कैन और खोजें",
            subtitle: "मतदाता पहचान पत्र पर कैमरा करें और स्वचालित रूप से विवरण प्राप्त करें।",
            startScanning: "स्कैनिंग शुरू करें",
            retakePhoto: "फिर से फोटो लें",
            scanModalTitle: "आईडी कार्ड स्कैन करें",
            cameraLabel: "कैमरा",
            autoScanning: "ऑटो-स्कैनिंग... स्थिर रहें",
            idFound: "आईडी मिली:",
            mobileNotAvailable: "मोबाइल नंबर उपलब्ध नहीं है।",
            voterNotFound: "आईडी {id} स्कैन की गई, लेकिन मतदाता विवरण नहीं मिला।",
            fetchError: "मतदाता विवरण प्राप्त करने में त्रुटि।",
            fetchFamilyFailed: "परिवार विवरण प्राप्त नहीं किया जा सका।",
            messageError: "संदेश बनाने में त्रुटि।",
            fetchingDetails: "मतदाता विवरण लाया जा रहा है...",
            readyToScan: "मतदाता रिकॉर्ड स्कैन करने के लिए तैयार",
            noActiveScan: "कोई सक्रिय स्कैन नहीं",
            clickToStart: '"स्कैनिंग शुरू करें" बटन पर क्लिक करें।',
            record: "रिकॉर्ड",
            n_a: "N/A",
            details: "विवरण :",
            fatherHusband: "पिता/पति :",
            sexAge: "लिंग/आयु :",
            mobile: "मोबाइल :",
            idCard: "पहचान पत्र :",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLocation: "मतदान स्थान :",
            call: "कॉल",
            text: "संदेश",
            slip: "स्लिप",
            prachar: "प्रचार",
            family: "परिवार",
            addToGroup: "ग्रूप में जोड़ें",
            addToGroupConfirm: "क्या आप वाकई इस सदस्य को अपने ग्रूप में जोड़ना चाहते हैं?",
            addToGroupSuccess: "ग्रूप में सफलतापूर्वक जोड़ा गया!",
            addToGroupFailed: "ग्रूप में जोड़ने में विफल।",
            addToGroupError: "ग्रूप में जोड़ते समय एक त्रुटि हुई।"
        },
        gu: {
            title: "સ્કેન અને શોધો",
            subtitle: "મતદાર ઓળખ કાર્ડ પર કેમેરા પોઇન્ટ કરો અને આપમેળે વિગતો મેળવો.",
            startScanning: "સ્કેનિંગ શરૂ કરો",
            retakePhoto: "ફરી ફોટો લો",
            scanModalTitle: "આઈડી આઇકાર્ડ સ્કેન કરો",
            cameraLabel: "કેમેરા",
            autoScanning: "ઓટો-સ્કેનિંગ... સ્થિર રહો",
            idFound: "આઈડી મળી:",
            mobileNotAvailable: "મોબાઇલ નંબર ઉપલબ્ધ નથી.",
            voterNotFound: "આઈડી {id} સ્કેન થઈ, પરંતુ મતદાર વિગતો મળી નથી.",
            fetchError: "મતદાર વિગતો મેળવવામાં ભૂલ.",
            fetchFamilyFailed: "કુટુંબ વિગતો મેળવી શકાઈ નહીં.",
            messageError: "સંદેશ બનાવવામાં ભૂલ.",
            fetchingDetails: "મતદાર વિગતો લાવવામાં આવી રહી છે...",
            readyToScan: "મતદાર રેકોર્ડ સ્કેન કરવા માટે તૈયાર",
            noActiveScan: "કોઈ સક્રિય સ્કેન નથી",
            clickToStart: '"સ્કેનિંગ શરૂ કરો" બટન પર ક્લિક કરો.',
            record: "રેકોર્ડ",
            n_a: "N/A",
            details: "વિગતો :",
            fatherHusband: "પિતા/પતિ :",
            sexAge: "લિંગ/ઉંમર :",
            mobile: "મોબાઇલ :",
            idCard: "ઓળખ કાર્ડ :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLocation: "મતદાન સ્થળ :",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            prachar: "પ્રચાર",
            family: "કુટુંબ",
            addToGroup: "જૂથમાં ઉમેરો",
            address: "સરનામું :",
            serialNo: "સર્ટિફિકેટ નં :",
            addToGroupConfirm: "શું તમે ખરેખર આ સભ્યને તમારા જૂથમાં ઉમેરવા માંગો છો?",
            addToGroupSuccess: "જૂથમાં સફળતાપૂર્વક ઉમેરાયું!",
            addToGroupFailed: "જૂથમાં ઉમેરવામાં નિષ્ફળ.",
            addToGroupError: "જૂથમાં ઉમેરતી વખતે એક ભૂલ આવી."
        }
    };

    const txt = t[lang];
    // --- Edit Mobile State ---
    const [isEditMobileOpen, setIsEditMobileOpen] = useState(false);
    const [editingVoter, setEditingVoter] = useState(null);
    const [newMobile, setNewMobile] = useState('');
    const [isSavingMobile, setIsSavingMobile] = useState(false);



    // --- Edit Mobile API Call ---
    const handleUpdateMobile = async () => {
        if (!newMobile || newMobile.length !== 10) {
            alert("Please enter a valid 10-digit mobile number.");
            return;
        }

        setIsSavingMobile(true);
        try {
            const payload = {
                app_id: appId?.toString(),
                // 🔴 FIX: અહિયાં id ની જગ્યાએ idcard_no પાસ કર્યું છે
                voter_id: editingVoter.idcard_no,
                mobile_no: newMobile,
                modify_by: userId?.toString() || "1"
            };

            const response = await axios.post(`${BASE_URL}/api/upd_voter_mobile`, payload);

            if (response.data.success || response.data.Success === "1") {
                alert("Mobile number updated successfully!");
                // નવો ડેટા લાવ્યા વગર જ સીધું UI અપડેટ કરો
                setEditingVoter(prev => prev.map(v =>
                    v.id === editingVoter.id ? { ...v, contact_no: newMobile } : v
                ));
                setIsEditMobileOpen(false);
            } else {
                alert("Failed to update mobile number.");
            }
        } catch (error) {
            console.error("Error updating mobile:", error);
            alert("An error occurred while updating the mobile number.");
        } finally {
            setIsSavingMobile(false);
        }
    };



    // --- 1. Load Cameras on Start ---
    const handleDevices = useCallback((mediaDevices) => {
        const videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput");
        setDevices(videoDevices);

        if (videoDevices.length > 0) {
            setSelectedDeviceId(videoDevices[0].deviceId);
        }
    }, []);

    useEffect(() => {
        if (!appId) {
            navigate("/");
            return;
        }
        navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, [handleDevices, appId, navigate]);

    // --- 2. Auto Capture & Process Loop ---
    useEffect(() => {
        let interval;
        if (!foundId && isModalOpen) {
            interval = setInterval(() => {
                if (webcamRef.current && !isScanning) {
                    const imageSrc = webcamRef.current.getScreenshot();
                    if (imageSrc) {
                        processImage(imageSrc);
                    }
                }
            }, 1500);
        }
        return () => clearInterval(interval);
    }, [foundId, isScanning, isModalOpen]);

    const preprocessImage = (dataUrl) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.filter = 'grayscale(100%) contrast(200%) brightness(110%)';
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg'));
            };
            img.src = dataUrl;
        });
    };

    const processImage = async (image) => {
        setIsScanning(true);
        try {
            const enhancedImage = await preprocessImage(image);
            const result = await Tesseract.recognize(enhancedImage, 'eng');
            const text = result.data.text;
            findTargetId(text, image);
        } catch (err) {
            console.error("Auto-scan error:", err);
        } finally {
            setIsScanning(false);
        }
    };

    // 🔴 FIX: Improved ID identification logic specifically for format like WQT3232089 (3 Letters + 7 Numbers)
    const findTargetId = (text, originalImage) => {
        // Keep only uppercase letters and numbers
        const cleanText = text.toUpperCase().replace(/[^A-Z0-9]/g, "");

        // Match any 10 character sequence
        const matches = cleanText.match(/[A-Z0-9]{10}/g);

        let finalId = null;

        if (matches && matches.length > 0) {
            for (let rawId of matches) {
                // First 3 are letters, Last 7 are numbers
                let prefix = rawId.substring(0, 3);
                let numbersPart = rawId.substring(3);

                // Fix OCR confusing numbers as letters in the prefix
                prefix = prefix
                    .replace(/0/g, "O")
                    .replace(/1/g, "I")
                    .replace(/5/g, "S")
                    .replace(/8/g, "B");

                // Fix OCR confusing letters as numbers in the numbers part
                numbersPart = numbersPart
                    .replace(/[OQD]/g, "0")
                    .replace(/[IL|T]/g, "1")
                    .replace(/Z/g, "2")
                    .replace(/B/g, "8")
                    .replace(/A/g, "4")
                    .replace(/S/g, "5")
                    .replace(/G/g, "6");

                const correctedId = prefix + numbersPart;

                // Validate exactly 3 letters followed by 7 numbers (Standard EPIC format)
                if (/^[A-Z]{3}[0-9]{7}$/.test(correctedId)) {
                    finalId = correctedId;
                    break; // Stop at the first valid ID found
                }
            }
        }

        if (finalId) {
            setFoundId(finalId);
            setImgSrc(originalImage);
            fetchVoterDetails(finalId);
        }
    };

    const fetchVoterDetails = async (idCardNo) => {
        if (!appId) return;
        setFetchingVoter(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/scan_and_search`, {
                app_id: appId,
                f_name: "",
                m_name: "",
                surname: "",
                mobile_no: "",
                id_card_no: idCardNo
            });

            if (response.data.success && response.data.data && response.data.data.result && response.data.data.result.length > 0) {
                setVoter(response.data.data.result[0]);
                setIsModalOpen(false);
            } else {
                alert(txt.voterNotFound.replace('{id}', idCardNo));
                setVoter(null);
                setImgSrc(null);
                setFoundId("");
            }
        } catch (error) {
            console.error("Error fetching voter details:", error);
            alert(txt.fetchError);
            setVoter(null);
            setImgSrc(null);
            setFoundId("");
        } finally {
            setFetchingVoter(false);
        }
    };

    // --- Add to Group API Call ---
    const handleAddToGroup = async (voter) => {
        // Show confirmation alert using translated text
        const isConfirmed = window.confirm(txt.addToGroupConfirm);

        if (!isConfirmed) {
            return; // User cancelled the operation
        }

        try {
            const payload = {
                app_id: appId?.toString(),
                user_id: userId?.toString() || appId?.toString(),
                idcard: voter.idcard_no
            };

            const response = await axios.post(`${BASE_URL}/api/ins_my_group`, payload);

            if (response.data.success) {
                alert(txt.addToGroupSuccess);
            } else {
                alert(txt.addToGroupFailed);
            }
        } catch (error) {
            console.error("Error adding to group:", error);
            alert(txt.addToGroupError);
        }
    };

    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available for this voter.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: userId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('સ્રી', 'સ્ત્રી')}\n`;
                    message += `${member.idcard_no}\n`;
                    message += `મતદાન સ્થળ : ${member.eng_polling_location}\n`;
                    message += `मतदान ता. : ૨૬-૦૪-૨૦૨૬, રવિવાર \n સમય : સવારે ૭.૦૦ થી સાંજે ૬.૦૦ સુધી\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;

                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');

            } else {
                alert("Could not fetch family details.");
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
            alert("Error generating message.");
        }
    };

    const handleSmsShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available for this voter.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_sms`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: userId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('સ્રી', 'સ્ત્રી')}\n`;
                    message += `${member.idcard_no}\n`;
                    message += `મતદાન સ્થળ : ${member.eng_polling_location}\n`;
                    message += `મતદાન તા. : ૨૬-૦૪-૨૦૨૬, રવિવાર \n સમય : સવારે ૭.૦૦ થી સાંજે ૬.૦૦ સુધી\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');

                window.location.href = `sms:${mobile}?body=${encodedMessage}`;

            } else {
                alert("Could not fetch family details for SMS.");
            }
        } catch (error) {
            console.error("Error generating SMS slip:", error);
            alert("Error generating SMS message.");
        }
    };

    const handleOpenScan = () => {
        setIsModalOpen(true);
        setFoundId("");
        setImgSrc(null);
        setVoter(null);
    };

    return (
        <>
            {/* --- SCANNER MODAL --- */}
            {isModalOpen && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-box" style={{ maxWidth: '500px' }}>
                        <div className="custom-modal-header">
                            <h2>{txt.scanModalTitle}</h2>
                            <button className="custom-close-btn" onClick={() => setIsModalOpen(false)}>✕</button>
                        </div>

                        <div className="custom-form-body">
                            {devices.length > 0 && (
                                <select
                                    className="modern-select mb-3"
                                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                                    value={selectedDeviceId}
                                >
                                    {devices.map((device, key) => (
                                        <option key={key} value={device.deviceId}>
                                            {device.label || `${txt.cameraLabel} ${key + 1}`}
                                        </option>
                                    ))}
                                </select>
                            )}

                            <div className="camera-frame-modern">
                                {imgSrc ? (
                                    <img src={imgSrc} alt="Captured" className="w-100 h-100 object-fit-cover" />
                                ) : (
                                    <Webcam
                                        audio={false}
                                        ref={webcamRef}
                                        screenshotFormat="image/jpeg"
                                        className="w-100 h-100 object-fit-cover"
                                        videoConstraints={{
                                            deviceId: selectedDeviceId,
                                            width: 1280,
                                            height: 720
                                        }}
                                    />
                                )}
                                {!imgSrc && <div className="scanner-guide"></div>}
                            </div>

                            <div className="text-center mt-3">
                                {!foundId ? (
                                    <p className="scan-status-text">
                                        <span className="dot-pulse"></span> {txt.autoScanning}
                                    </p>
                                ) : (
                                    <div>
                                        <p className="text-success fw-bold mb-2">{txt.idFound} {foundId}</p>
                                        <button className="btn-secondary w-100" onClick={() => { setImgSrc(null); setFoundId(""); setVoter(null); }}>
                                            {txt.retakePhoto}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className={`app-container p-4 ${isModalOpen ? 'blur-background' : ''}`}>

                <Header title={txt.title} />

                {/* Mobile Number Edit Modal */}
                {isEditMobileOpen && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                        <div className="slide-up-element" style={{ background: 'white', padding: '24px', borderRadius: '16px', width: '90%', maxWidth: '350px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                            <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>Update Mobile Number</h4>
                            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b' }}>{editingVoter?.eng_f_name} {editingVoter?.eng_surname}</p>

                            <input
                                type="tel"
                                maxLength="10"
                                value={newMobile}
                                onChange={(e) => setNewMobile(e.target.value.replace(/[^0-9]/g, ''))}
                                style={{ width: '100%', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '10px', marginBottom: '24px', fontSize: '15px', outline: 'none' }}
                                placeholder="Enter 10-digit mobile number"
                                autoFocus
                            />

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button
                                    onClick={() => setIsEditMobileOpen(false)}
                                    style={{ padding: '10px 16px', background: 'white', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateMobile}
                                    disabled={isSavingMobile}
                                    style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                    {isSavingMobile ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                {/* --- PROFESSIONAL ACTION BAR --- */}
                <div className="filter-container">
                    <div className="search-wrapper-placeholder">
                        <p className="m-0 text-muted small">{txt.readyToScan}</p>
                    </div>
                    <div className="action-group">
                        <button onClick={handleOpenScan} className="btn-add-user">
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="me-2">
                                <path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {txt.startScanning}
                        </button>
                    </div>
                </div>

                {/* --- RESULTS AREA --- */}
                <div className="d-flex justify-content-center mt-2">
                    {fetchingVoter && (
                        <div className="text-center p-5">
                            <div className="spinner"></div>
                            <p className="mt-3 text-muted fw-bold">{txt.fetchingDetails}</p>
                        </div>
                    )}

                    {voter && !fetchingVoter && (
                        <div className="voter-card-clean shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
                            <div className="voter-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <span className="serial-number">{txt.record}</span>
                                    <span className="voter-name-bold ms-2">
                                        {voter.full_name ? voter.full_name.replace("نام :", "").trim() : txt.n_a}
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleAddToGroup(voter)}
                                    style={{
                                        backgroundColor: '#eff6ff',
                                        color: '#2563eb',
                                        border: '1px solid #bfdbfe',
                                        borderRadius: '20px',
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseOver={(e) => { e.target.style.backgroundColor = '#dbeafe'; }}
                                    onMouseOut={(e) => { e.target.style.backgroundColor = '#eff6ff'; }}
                                >
                                    + {txt.addToGroup}
                                </button>
                            </div>

                            <div className="voter-body position-relative">
                                <div
                                    className="map-icon-wrapper"
                                    style={{ cursor: 'pointer', zIndex: 10 }}
                                    title="View on Google Maps"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const address = voter.eng_localityid;
                                        if (address && address !== "N/A") {
                                            // ગુગલ મેપ્સમાં એડ્રેસ સર્ચ કરવા માટેની લિંક
                                            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
                                        } else {
                                            alert("Address not available!");
                                        }
                                    }}
                                >
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                </div>
                                <div className="info-row"><span className="label">{txt.details}</span><span className="value">{voter.first_detail || txt.n_a}</span></div>
                                <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.middle_name ? voter.middle_name.replace("पिता/पति :", "").trim() : txt.n_a}</span></div>
                                <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{voter.slnoinpart || "N/A"}</span></div>
                                <div className="info-row"><span className="label">{txt.address}</span><span className="value">{voter.eng_localityid || "N/A"}</span></div>
                                <div className="info-row"><span className="label">{txt.sexAge}</span><span className="value">{voter.sex_age ? voter.sex_age.replace("जाति/आयु :", "").trim() : txt.n_a}</span></div>
                                <div className="info-row" style={{ display: 'flex', alignItems: 'center' }}>
                                    <span className="label">{txt.mobile}</span>
                                    <span className="value mobile-value" style={{ flexGrow: 1 }}>{voter.contact_no || "N/A"}</span>
                                    <svg
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingVoter(voter);
                                            setNewMobile(voter.contact_no || '');
                                            setIsEditMobileOpen(true);
                                        }}
                                        style={{ cursor: 'pointer', color: '#f59e0b', width: '18px', height: '18px', flexShrink: 0 }}
                                        fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                </div>
                                <div className="info-row"><span className="label">{txt.idCard}</span><span className="value">{voter.idcard_no ? voter.idcard_no.replace("वोटर आईडी नंबर :", "").trim() : foundId}</span></div>
                                <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || voter.booth_no || txt.n_a}</span></div>
                                <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || txt.n_a}</span></div>
                                <div className="info-row"><span className="label">{txt.pollingLocation}</span><span className="value">{voter.polling_location ? voter.polling_location.replace("मतदान स्थल :", "").trim() : "-"}</span></div>
                            </div>

                            <div className="voter-actions">
                                <ActionButton icon="📞" color="#0ea5e9" label={txt.call}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!voter.contact_no || voter.contact_no === "N/A" || voter.contact_no === "-" || voter.contact_no === "") {
                                            alert("Mobile number is not available for this voter.");
                                        }
                                        else {
                                            window.open(`tel:${voter.contact_no}`, '_blank');
                                        }
                                    }}

                                />
                                <ActionButton icon="✉️" color="#f59e0b" label={txt.text} onClick={() => handleSmsShare(voter)} />
                                <WhatsappSlipButton voter={voter} />
                                <ActionButton
                                    icon="🖨"
                                    color="#f97316"
                                    label={txt.print}
                                    onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                />
                                <PracharButton voter={voter} />
                                <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} onClick={() => navigate('/dis_family_member', { state: { voter_id: voter.id } })} />
                            </div>
                        </div>
                    )}

                    {!voter && !fetchingVoter && !isModalOpen && (
                        <div className="no-record-placeholder">
                            <div className="icon-wrap-muted mb-3">
                                <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 11V7m0 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <h4>{txt.noActiveScan}</h4>
                            <p>{txt.clickToStart}</p>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                /* --- MODAL OVERLAY & BOX --- */
                .custom-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15, 23, 42, 0.75); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10000; padding: 20px; }
                .custom-modal-box { background: white; border-radius: 16px; width: 100%; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden; }
                .custom-modal-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .custom-modal-header h2 { margin: 0; font-size: 1.25rem; color: #0f172a; font-weight: 700; }
                .custom-close-btn { background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 1.5rem; }
                .custom-form-body { padding: 24px; }
                .custom-modal-footer { padding: 16px 24px; background: #f8fafc; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }
                
                /* --- ACTION BAR --- */
                .filter-container { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 24px; }
                .search-wrapper-placeholder { flex: 1; }
                .btn-add-user { display: flex; align-items: center; justify-content: center; background-color: #2563eb; color: white; padding: 12px 24px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: 0.2s; white-space: nowrap; box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2); }
                .btn-add-user:hover { background-color: #1d4ed8; transform: translateY(-1px); }

                /* --- CAMERA & SCANNER --- */
                .camera-frame-modern { position: relative; width: 100%; height: 300px; background: #000; border-radius: 12px; overflow: hidden; border: 3px solid #e2e8f0; }
                .modern-select { width: 100%; padding: 12px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; outline: none; background-color: #f8fafc; }
                .scanner-guide { position: absolute; top: 15%; left: 10%; right: 10%; bottom: 15%; border: 2px dashed rgba(255, 255, 255, 0.5); border-radius: 8px; pointer-events: none; }
                .scan-status-text { color: #2563eb; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 10px; }
                .dot-pulse { width: 8px; height: 8px; background: #ef4444; border-radius: 50%; animation: pulse 1s infinite; }
                @keyframes pulse { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.5); opacity: 0.5; } 100% { transform: scale(1); opacity: 1; } }

                /* --- MISC --- */
                .no-record-placeholder { text-align: center; padding: 60px 20px; color: #64748b; background: white; border-radius: 16px; border: 1px dashed #e2e8f0; width: 100%; max-width: 500px; }
                .icon-wrap-muted { color: #cbd5e1; }
                .blur-background { filter: blur(4px); pointer-events: none; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 640px) {
                    .filter-container { flex-direction: column; align-items: stretch; text-align: center; }
                    .btn-add-user { width: 100%; }
                }
            `}</style>
        </>
    );
};

const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }

    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default ScanId;